/**
 * Tema claro/escuro. Escuro é o padrão da marca; "sistema" segue o sistema
 * operacional. A escolha fica no navegador e é aplicada também por um
 * script em index.html, antes do primeiro paint — senão a página piscaria
 * escura antes de ficar clara.
 */
import { useSyncExternalStore } from 'react';

export type ThemePreference = 'dark' | 'light' | 'system';
const KEY = 'centralone:tema';
const listeners = new Set<() => void>();
const media = window.matchMedia('(prefers-color-scheme: light)');

function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'system' || v === 'dark' ? v : 'dark';
  } catch {
    return 'dark';
  }
}

let preference = readPreference();

export function resolvedTheme(pref = preference): 'dark' | 'light' {
  if (pref === 'system') return media.matches ? 'light' : 'dark';
  return pref;
}

function apply() {
  const root = document.documentElement;
  root.classList.add('theme-switching');
  root.classList.toggle('light', resolvedTheme() === 'light');
  // Dois frames: deixa o navegador pintar o tema novo sem animar a troca.
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('theme-switching')));
  listeners.forEach((l) => l());
}

media.addEventListener('change', () => preference === 'system' && apply());

export function setTheme(next: ThemePreference) {
  preference = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Sem armazenamento, vale só nesta aba.
  }
  apply();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useTheme() {
  const pref = useSyncExternalStore(subscribe, () => preference);
  const resolved = useSyncExternalStore(subscribe, () => resolvedTheme());
  return { preference: pref, resolved, setTheme };
}
